export const REQUEST_ID_HEADER = 'X-Request-Id';

const REFERENCE_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const REFERENCE_LENGTH = 8;

/**
 * Short reference for one logical request: 8 characters of [A-Z0-9].
 * Sent as X-Request-Id so support can find the request in the API logs.
 */
export function createRequestReference(): string {
  const bytes = new Uint8Array(REFERENCE_LENGTH);

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < REFERENCE_LENGTH; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  let reference = '';
  for (const byte of bytes) {
    reference += REFERENCE_ALPHABET[byte % REFERENCE_ALPHABET.length];
  }

  return reference;
}

export const REQUEST_TIMEOUT_MS = 30000;

export type RequestFailureKind =
  'offline' | 'unreachable' | 'timeout' | 'server' | 'api';

interface ClassifyRequestFailureInput {
  timedOut: boolean;
  online: boolean;
  /** set when fetch itself rejected (no response arrived) */
  error?: unknown;
  /** status of a response that did arrive */
  status?: number;
  /** the response body carried a title, detail or errors */
  hasDetail?: boolean;
}

/**
 * Why a request failed. Precedence: a timeout beats everything; a rejected
 * fetch is offline when the browser says so, else unreachable; a 5xx without
 * details is a server failure; anything else is an API error.
 */
export function classifyRequestFailure({
  timedOut,
  online,
  error,
  status,
  hasDetail,
}: ClassifyRequestFailureInput): RequestFailureKind {
  if (timedOut) return 'timeout';
  if (error && !online) return 'offline';
  if (error) return 'unreachable';
  if (status !== undefined && status >= 500 && !hasDetail) return 'server';

  return 'api';
}

export const REQUEST_FAILURE_MESSAGES = {
  offline:
    "You're offline. Reconnect to the internet and try again. Nothing has been lost.",
  unreachable:
    "We couldn't reach the FMI portal, so your changes haven't been saved yet. Check your internet connection and try again. If this keeps happening, try a different browser or device.",
  server: 'Something went wrong on our side. Please try again in a few minutes.',
  timeout: 'The portal is taking too long to respond. Please try again.',
} as const;

export type RequestFailureMessageKind = keyof typeof REQUEST_FAILURE_MESSAGES;

export function isRequestFailureMessageKind(
  kind: unknown,
): kind is RequestFailureMessageKind {
  return (
    typeof kind === 'string' &&
    Object.prototype.hasOwnProperty.call(REQUEST_FAILURE_MESSAGES, kind)
  );
}

/** The message for a failure kind, ending with the reference support can look up. */
export function formatRequestFailure(
  kind: RequestFailureMessageKind,
  reference: string,
): string {
  return `${REQUEST_FAILURE_MESSAGES[kind]} Reference: ${reference}`;
}

export const RETRY_DELAY_MS = 1000;

/** The drawing save is idempotent (the whole drawing is replaced), so it may be retried. */
export const SAVE_DRAWING_RE = /^\/claim\/[^/]+\/save-drawing(\?|$)/;

/**
 * The retry allowlist: only a request that got no response, and only reads and
 * the drawing save. A retried write elsewhere could be applied twice.
 */
export function shouldRetry(
  method: string | undefined,
  url: string,
  kind: RequestFailureKind,
): boolean {
  if (kind !== 'unreachable' && kind !== 'timeout') return false;
  if (method === undefined || method === 'GET') return true;

  return method === 'PUT' && SAVE_DRAWING_RE.test(url);
}
